import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

class Tabs {
  constructor(config) {
    this.config = config
    this.intervalID = null
    this.initialized = false

    // Track the current tab index so it doesn't reset on each re-entry
    this.currentTabIndex = 0
    this.autoChangeManuallyStopped = false

    this.init()
  }

  // Run once on creation
  init() {
    if (!this.validate()) return
    this.openTabsOnClick()
    this.emptyLoader() // Ensure the initial loader DOM is in place
  }

  // Called by ScrollTrigger when entering the viewport
  onEnter() {
    // console.log('Tabs onEnter - Initialized:', this.initialized)
    if (!this.initialized) {
      this.initialized = true
      if (this.config.loading && this.config.loadingColor) {
        this.changeLoaderColor()
      }
      // Start auto-change only once on initial enter
      this.startAutoChange(true)
    } else {
      // For subsequent entries, just resume loader and auto-change if needed
      this.resumeLoader()
      if (
        !this.intervalID &&
        this.config.autoplay &&
        !this.autoChangeManuallyStopped
      ) {
        this.startAutoChange()
      }
    }
  }

  // Validate config
  validate() {
    if (!(this.config && this.config.el)) {
      // console.error('Tabs: Please provide an el to initialize tabs')
      return false
    }
    if (!document.querySelector(this.config.el)) {
      // console.error(
      //   `Tabs: Couldn't find an element with selector ${this.config.el}`
      // )
      return false
    }
    if (typeof this.config.timer === 'string') {
      // console.error('Tabs: Timer must be a number')
      return false
    }
    if (typeof this.config.autoplay === 'string') {
      // console.error('Tabs: autoplay must be boolean')
      return false
    }
    if (typeof this.config.loading === 'string') {
      // console.error('Tabs: loading must be boolean')
      return false
    }
    return true
  }

  getItems() {
    const mainContainer = document.querySelector(this.config.el)
    return {
      mainContainer,
      allTabs: mainContainer?.querySelectorAll('.tabs-container [data-tab]') || [],
      allTabsContent: mainContainer?.querySelectorAll('.tab-content'),
    }
  }

  openTabsOnClick() {
    const { allTabs } = this.getItems()
    allTabs.forEach((tabButton, index) => {
      tabButton.addEventListener('click', () => {
        // Pause auto-change during manual interaction
        this.stopAutoChange()
        this.autoChangeManuallyStopped = true

        const dataTab = tabButton.dataset.tab
        this.contentChange(dataTab)

        if (this.config.loading) {
          this.loader()
        }

        // Optionally resume auto-change
        this.startAutoChange()
      })
    })
  }

  contentChange(tabId) {
    const items = this.getItems()
    const newActiveTab = items.mainContainer.querySelector(
      `[data-tab="${tabId}"]`
    )
    const newActiveTabContent = items.mainContainer.querySelectorAll(
      `[data-tab-content="${tabId}"]`
    )

    // Remove active classes
    items.allTabs.forEach((tab) => tab.classList.remove('active-tab'))
    items.allTabsContent.forEach((ct) => ct.classList.remove('active-tab'))

    // Add active classes to newly selected tab
    newActiveTabContent.forEach((ct) => ct.classList.add('active-tab'))
    newActiveTab.classList.add('active-tab')

    // Update our stored currentTabIndex
    const newIndex = Array.from(items.allTabs).indexOf(newActiveTab)
    if (newIndex >= 0) {
      this.currentTabIndex = newIndex
    }
  }

  changeLoaderColor() {
    const { allTabs } = this.getItems()
    allTabs.forEach((tab) => {
      tab.querySelectorAll('.tabs--loader').forEach((loaderEl) => {
        loaderEl.style.background = this.config.loadingColor
      })
    })
  }

  // Inserts a minimal loader DOM if needed
  emptyLoader() {
    const loaderEl = `<div class="tabs--loader-container w-1 h-full max-h-24 bg-lavender-light absolute left-0 lg:-left-6 rounded-full top-0">
      <div class="relative w-1 h-full">
        <div class="tabs--loader top-0 bg-tiger rounded-full w-1 h-1"></div>
      </div>
    </div>`

    const { allTabs } = this.getItems()
    // Only add a loader to the first tab if it doesn't exist
    if (
      allTabs.length > 0 &&
      !allTabs[0].querySelector('.tabs--loader-container')
    ) {
      allTabs[0].innerHTML += loaderEl
    }
  }

  // Applies the loader animation to the active tab
  loader() {
    if (!this.config.loading) return

    const timer = this.config.timer || 5000
    // Build the loader HTML with a custom duration
    const loaderEl = `
      <div class="tabs--loader-container w-1 h-full max-h-24 bg-lavender-light absolute left-0 lg:-left-6 rounded-full top-0">
        <div class="relative w-1 h-full">
          <div class="tabs--loader top-0 bg-tiger rounded-full w-1"
            style="animation-duration: ${timer}ms;">
          </div>
        </div>
      </div>`

    const { allTabs } = this.getItems()

    allTabs.forEach((tab, index) => {
      // First remove any old loader on ALL tabs
      const oldLoader = tab.querySelector('.tabs--loader-container')
      if (oldLoader) {
        oldLoader.remove()
      }

      // Only inject a new loader into the currently active tab
      if (index === this.currentTabIndex) {
        tab.style.position = 'relative'
        tab.insertAdjacentHTML('beforeend', loaderEl)

        // Listen for the CSS 'animationend' on the newly added loader
        const newLoader = tab.querySelector('.tabs--loader')
        if (newLoader) {
          newLoader.addEventListener(
            'animationend',
            () => {
              // As soon as the loader finishes, jump to next tab
              this.goToNextTab()
            },
            { once: true }
          )
        }
      }
    })
  }

  goToNextTab() {
    // Move this.currentTabIndex to the next tab
    // (cycle back to 0 if we're at the last tab)
    const items = this.getItems()
    this.currentTabIndex = (this.currentTabIndex + 1) % items.allTabs.length

    // Find the data-tab for the newly active tab
    const newTabId = items.allTabs[this.currentTabIndex].dataset.tab
    // Re-run contentChange to show the next tab's content
    this.contentChange(newTabId)
    // Re-run loader to attach a loader to the newly active tab
    this.loader()
  }

  // Creates an interval to auto-change tabs
  startAutoChange(fromOnEnter = false) {
    // console.log(
    //   'Tabs startAutoChange - intervalID:',
    //   this.intervalID,
    //   'fromOnEnter:',
    //   fromOnEnter
    // )
    // Only start if autoplay is on
    if (!this.config.autoplay) return

    // If it's already running, do nothing
    if (this.intervalID) return

    // Make sure to update the loader each time we start
    this.loader()

    const items = this.getItems()
    const timer = this.config.timer || 5000

    // Create interval
    this.intervalID = setInterval(() => {
      // Cycle to the next tab index
      this.currentTabIndex++
      if (this.currentTabIndex >= items.allTabs.length) {
        this.currentTabIndex = 0
      }
      // Get the new tab's data-tab attribute
      const newTabId = items.allTabs[this.currentTabIndex].dataset.tab
      this.contentChange(newTabId)
      this.loader()
      if (this.config.loadingColor) {
        this.changeLoaderColor()
      }
    }, timer)

    if (fromOnEnter) {
      this.autoChangeManuallyStopped = false
    }
  }

  stopAutoChange() {
    // console.log('Tabs stopAutoChange - intervalID:', this.intervalID)
    if (this.intervalID) {
      clearInterval(this.intervalID)
      this.intervalID = null
    }
  }

  pauseLoader() {
    // console.log('Tabs pauseLoader')
    const { allTabs } = this.getItems()
    allTabs.forEach((tab) => {
      const loader = tab.querySelector('.tabs--loader')
      if (loader) {
        loader.style.animationPlayState = 'paused'
      }
    })
  }

  resumeLoader() {
    // console.log('Tabs resumeLoader')
    const { allTabs } = this.getItems()
    allTabs.forEach((tab) => {
      const loader = tab.querySelector('.tabs--loader')
      if (loader) {
        loader.style.animationPlayState = 'running'
      }
    })
  }
}

// Our Stimulus controller
export default class extends Controller {
  initialize() {
    this.initializeTabs = this.initializeTabs.bind(this)
    super.initialize()
  }

  connect() {
    requestAnimationFrame(this.initializeTabs)
  }

  disconnect() {
    this.cleanup()
    super.disconnect()
  }

  cleanup() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill()
      this.scrollTrigger = null
    }

    if (this.tabs) {
      this.tabs.stopAutoChange()
      this.tabs.pauseLoader()
      this.tabs = null
    }
  }

  initializeTabs() {
    this.cleanup()

    const tabsElement = document.querySelector('#tabs')

    if (!tabsElement) {
      console.debug('Tabs element not found')
      return
    }

    this.tabs = new Tabs({
      el: '#tabs',
      loading: true,
      autoplay: true,
      timer: 5000,
    })

    this.scrollTrigger = ScrollTrigger.create({
      trigger: '.tabs-container',
      start: 'top 80%',
      end: 'bottom 20%',
      onEnter: () => {
        console.debug('ScrollTrigger: onEnter → resumeLoader')
        if (this.tabs) {
          this.tabs.onEnter()
        }
      },
      onEnterBack: () => {
        console.debug('ScrollTrigger: onEnterBack → resumeLoader')
        if (this.tabs) {
          this.tabs.resumeLoader()
        }
      },
      onLeave: () => {
        console.debug('ScrollTrigger: onLeave → pauseLoader')
        if (this.tabs) {
          this.tabs.stopAutoChange()
          this.tabs.pauseLoader()
        }
      },
      onLeaveBack: () => {
        console.debug('ScrollTrigger: onLeaveBack → pauseLoader')
        if (this.tabs) {
          this.tabs.stopAutoChange()
          this.tabs.pauseLoader()
        }
      },
      toggleActions: 'play none none none',
    })
  }
}
